<script lang="ts">
  /**
   * ShareItemChips — "What goes out" on the share sheet (lane share-chips,
   * 2026-09-12). Scott: "it's a lot when you share something, to put three
   * links into text. it feels like we need more configurability here."
   *
   * Three toggle chips for the three things a share can hand iOS:
   *   Image → the 9:16 card PNG        (navigator.share `files`)
   *   Link  → the story URL            (navigator.share `url`)
   *   Story → the one-line story text  (navigator.share `text`)
   *
   * Sublabels are fixed nouns (share.ts SHARE_ITEM_CHIPS) that must fit the chip
   * uncut; scripts/verify-share-chips-fit.mjs measures them at 390 px.
   *
   * On = filled accent, off = outline. The rules (default Image only, the last
   * lit chip cannot be turned off, the summary) live in share.ts so they are
   * tested; this file only draws them.
   */
  import { haptic } from "../lib/haptics";
  import { SHARE_ITEM_CHIPS, shareItemsSummary, toggleShareItem, type ShareItemKey, type ShareItems } from "../lib/share";

  let { items = $bindable<ShareItems>() }: { items: ShareItems } = $props();

  function toggle(key: ShareItemKey) {
    const next = toggleShareItem(items, key);
    if (next === items) {
      haptic("error");
      return;
    }
    haptic("selection");
    items = next;
  }

  let summary = $derived(shareItemsSummary(items));
</script>

<div class="chips-block" data-share-items={summary}>
  <p class="chips-label">What goes out <span class="chips-summary">· {summary}</span></p>
  <div class="chips" role="group" aria-label="What goes out">
    {#each SHARE_ITEM_CHIPS as c (c.key)}
      <button
        type="button"
        class="chip"
        class:on={items[c.key]}
        data-chip={c.key}
        aria-pressed={items[c.key]}
        aria-label={`${c.text}, ${c.hint}`}
        onclick={() => toggle(c.key)}
      >
        <span class="chip-t">{c.text}</span>
        <span class="chip-h">{c.hint}</span>
      </button>
    {/each}
  </div>
</div>

<style>
  .chips-block {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .chips-label {
    margin: 0;
    font: var(--type-caption);
    color: var(--text-muted);
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .chips-summary {
    text-transform: none;
    letter-spacing: 0;
    color: var(--text-body);
  }
  .chips {
    display: flex;
    gap: var(--space-2);
  }
  .chip {
    flex: 1 1 0;
    min-width: 0;
    min-height: 44px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1px;
    padding: 6px 10px;
    border-radius: var(--radius-pill);
    border: 1px solid var(--divider-strong);
    background: transparent;
    color: var(--text-muted);
    font-family: var(--font-text);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: background-color var(--dur-micro) var(--ease-swim),
                border-color var(--dur-micro) var(--ease-swim),
                color var(--dur-micro) var(--ease-swim);
  }
  .chip-t {
    font-size: 15px;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
  }
  .chip-h {
    font-size: 11px;
    line-height: 1.2;
    opacity: 0.75;
    white-space: nowrap;
  }
  .chip.on {
    background: var(--accent-primary);
    border-color: var(--accent-primary);
    color: var(--text-on-accent);
  }
  .chip:focus-visible {
    outline: 2px solid var(--accent-yellow);
    outline-offset: 2px;
  }
</style>
